const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://your-api-url.com/api';

// הצג את כתובת ה-API בקונסול (רק בפיתוח)
if (process.env.NODE_ENV === 'development') {
  console.log('🔗 כתובת API:', API_BASE_URL);
  if (API_BASE_URL === 'https://your-api-url.com/api' || !API_BASE_URL) {
    console.warn('⚠️ אזהרה: כתובת ה-API לא מוגדרת!');
    console.warn('אנא צור קובץ .env בשורש הפרויקט והוסף:');
    console.warn('REACT_APP_API_URL=https://your-actual-api-url.com/api');
  }
}

// שמירת token ב-localStorage
const getToken = () => localStorage.getItem('authToken');
const setToken = (token) => localStorage.setItem('authToken', token);
const removeToken = () => localStorage.removeItem('authToken');

// שמירת user info
const getUserInfo = () => {
  const userStr = localStorage.getItem('userInfo');
  return userStr ? JSON.parse(userStr) : null;
};

const setUserInfo = (userInfo) => {
  localStorage.setItem('userInfo', JSON.stringify(userInfo));
};

const removeUserInfo = () => {
  localStorage.removeItem('userInfo');
};

// פונקציה לזיהוי שגיאות מערכת פנימיות
function isSystemError(errorMessage) {
  if (!errorMessage) return false;
  
  const systemErrorPatterns = [
    /SQL Server/i,
    /database/i,
    /connection.*SQL/i,
    /Named Pipes Provider/i,
    /Could not open a connection/i,
    /network-related.*error/i,
    /instance-specific error/i,
    /server was not found/i,
    /remote connections/i,
    /provider.*error/i,
    /internal server error/i,
    /database.*error/i,
    /connection string/i,
    /timeout.*database/i,
    /database.*timeout/i
  ];
  
  return systemErrorPatterns.some(pattern => pattern.test(errorMessage));
}

// פונקציה כללית ל-API calls
async function apiCall(endpoint, options = {}) {
  const token = getToken();
  
  // בדוק אם כתובת ה-API מוגדרת
  if (API_BASE_URL === 'https://your-api-url.com/api' || !API_BASE_URL) {
    throw new Error('כתובת השרת לא מוגדרת. אנא הגדר את REACT_APP_API_URL בקובץ .env');
  }
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };
  
  const fullUrl = `${API_BASE_URL}${endpoint}`;
  
  try {
    console.log('מנסה להתחבר ל:', fullUrl);
    
    const response = await fetch(fullUrl, config);
    
    if (response.status === 401) {
      // נסה לקרוא את ה-error מהתגובה
      try {
        const errorData = await response.json();
        // אם יש token - זה אומר שההתחברות פגה
        if (token) {
          removeToken();
          removeUserInfo();
          window.location.href = '/';
          throw new Error('ההתחברות פגה. אנא התחבר שוב.');
        }
        // אחרת - זו שגיאת לוגאין
        throw new Error(errorData.error || 'שם משתמש או סיסמה אינם תקינים');
      } catch (parseError) {
        // אם לא הצלחנו לקרוא את ה-JSON, נזרוק שגיאה כללית
        throw new Error('שם משתמש או סיסמה אינם תקינים');
      }
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'שגיאה בשרת' }));
      const errorMessage = error.error || `שגיאת שרת: ${response.status} ${response.statusText}`;
      
      // בדוק אם זו שגיאת מערכת פנימית (SQL Server, database, etc.)
      if (isSystemError(errorMessage)) {
        // שמור את השגיאה המפורטת בקונסול למנהל מערכת
        console.error('שגיאת מערכת מפורטת:', errorMessage);
        console.error('פרטים נוספים:', {
          endpoint,
          status: response.status,
          timestamp: new Date().toISOString()
        });
        // החזר הודעה כללית למשתמש
        throw new Error('תקלת מערכת, אנא פנה למנהל המערכת');
      }
      
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    console.error('URL:', fullUrl);
    
    // טיפול בשגיאות ספציפיות
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      // זה אומר שהשרת לא זמין או שיש בעיית CORS
      throw new Error(
        `תקלה בהתחברות לשרת , אנא פנה למנהל השרות או נסה שוב מאוחר יותר`
      );
    }
    
    // בדוק אם זו שגיאת מערכת פנימית
    if (error.message && isSystemError(error.message)) {
      // שמור את השגיאה המפורטת בקונסול למנהל מערכת
      console.error('שגיאת מערכת מפורטת:', error.message);
      console.error('פרטים נוספים:', {
        endpoint,
        url: fullUrl,
        timestamp: new Date().toISOString(),
        errorStack: error.stack
      });
      // החזר הודעה כללית למשתמש
      throw new Error('תקלת מערכת, אנא פנה למנהל המערכת');
    }
    
    // אם יש הודעת שגיאה ספציפית - השתמש בה
    if (error.message) {
      throw error;
    }
    
    // אחרת - שגיאה כללית
    throw new Error(`שגיאה בהתחברות לשרת: ${error.message || 'שגיאה לא ידועה'}`);
  }
}

// API functions
export const api = {
  // Login
  async login(user, password) {
    try {
      const response = await apiCall('/Auth/login', {
        method: 'POST',
        body: JSON.stringify({ user: user, password: password }),
      });
      
      if (response.success) {
        setToken(response.token);
        setUserInfo({
          name: response.name,
          type: response.type,
          userCode: user,
          contracts: response.contracts,
          dashboard: response.dashboard,
        });
      } else {
        // אם השרת מחזיר success: false עם error
        if (response.error) {
          throw new Error(response.error);
        }
      }
      
      return response;
    } catch (error) {
      // אם יש כבר הודעת שגיאה - זרוק אותה
      if (error.message) {
        throw error;
      }
      // אחרת - שגיאה כללית
      throw new Error('שגיאה בהתחברות. נסה שוב.');
    }
  },
  
  // Logout
  logout() {
    removeToken();
    removeUserInfo();
  },
  
  // Get user info
  getUserInfo,
  
  // Reports
  async getDocumentsReport(userCode, userType, contractId) {
    return apiCall('/reports/documents', {
      method: 'POST',
      body: JSON.stringify({
        UserCode: userCode,
        UserType: userType,
        ContractId: contractId,
      }),
    });
  },
  
  async getTenantsReport(userCode, userType, contractId) {
    return apiCall('/reports/tnnts', {
      method: 'POST',
      body: JSON.stringify({
        UserCode: userCode,
        UserType: userType,
        ContractId: contractId,
      }),
    });
  },
  
  async getPaymentsReport(userCode, userType, contractId) {
    return apiCall('/reports/payments', {
      method: 'POST',
      body: JSON.stringify({
        UserCode: userCode,
        UserType: userType,
        ContractId: contractId,
      }),
    });
  },
  
  async getProfitabilityReport(userCode, userType, contractId) {
    return apiCall('/reports/prftRpt', {
      method: 'POST',
      body: JSON.stringify({
        UserCode: userCode,
        UserType: userType,
        ContractId: contractId,
      }),
    });
  },
  
  async getBoqReport(userCode, userType, contractId) {
    return apiCall('/reports/boq', {
      method: 'POST',
      body: JSON.stringify({
        UserCode: userCode,
        UserType: userType,
        ContractId: contractId,
      }),
    });
  },

  async saveBoqReport(userCode, userType, contractId, data) {
    return apiCall('/reports/boq/save', {
      method: 'POST',
      body: JSON.stringify({
        UserCode: userCode,
        UserType: userType,
        ContractId: contractId,
        buildings: data.buildings,
        tenants: data.tenants,
        boqItems: data.boqItems,
      }),
    });
  },
  
  async getReport(userCode, userType, contractId) {
    return apiCall('/reports/report', {
      method: 'POST',
      body: JSON.stringify({
        UserCode: userCode,
        UserType: userType,
        ContractId: contractId,
      }),
    });
  },
  
  async getContractData(userCode, userType, contractId) {
    return apiCall('/reports/contractData', {
      method: 'POST',
      body: JSON.stringify({
        UserCode: userCode,
        UserType: userType,
        ContractId: contractId,
      }),
    });
  },
};
